import { useTheme } from "@/context/ThemeContext";
import { ThemeMenu } from "@/components/ThemeMenu";
import { VisionAccessibilitySections } from "@/components/VisionAccessibilitySections";
import { ReadingStyleSection } from "@/components/ReadingStyleSection";
import {
  PAPER_TEXTURES,
  type PaperTexture,
  FONT_SCALES,
  FONT_SCALE_LABELS,
  FONT_SCALE_VALUES,
  type FontScale,
} from "@/lib/themes";

// ─────────────────────────────────────────────────────────────────────────────
// ThemeStudio — minimal theme customization editor.
//
// Sections:
//   1. Palette       — theme picker (ThemeMenu)
//   2. Paper texture — Off / Subtle / Full grain on the paper background
//   3. Reset
//
// All changes write through useTheme().setPrefs(), which persists to
// localStorage instantly and syncs to profiles.theme_prefs in the background.
// ─────────────────────────────────────────────────────────────────────────────

const TEXTURE_LABELS: Record<PaperTexture, string> = {
  none: "Off",
  subtle: "Subtle",
  full: "Full",
};

const TEXTURE_HINTS: Record<PaperTexture, string> = {
  none: "Flat paper — no grain or noise overlay.",
  subtle: "Light grain over the paper background.",
  full: "Full grain texture — most noticeable on warm themes.",
};

export function ThemeStudio() {
  const { prefs, setPrefs, resetPrefs } = useTheme();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {/* Palette */}
      <div role="group" aria-labelledby="theme-studio-palette-label">
        <SubLabel id="theme-studio-palette-label">Palette</SubLabel>
        <ThemeMenu compact />
      </div>

      {/* Paper texture */}
      <div
        role="radiogroup"
        aria-labelledby="theme-studio-texture-label"
        aria-disabled={prefs.visionMode === "high_contrast"}
        style={{ opacity: prefs.visionMode === "high_contrast" ? 0.5 : 1 }}
      >
        <SubLabel id="theme-studio-texture-label">Paper texture</SubLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {PAPER_TEXTURES.map((id, i) => {
            const active = prefs.paperTexture === id;
            const disabled = prefs.visionMode === "high_contrast";
            return (
              <button
                key={id}
                type="button"
                role="radio"
                disabled={disabled}
                onClick={() => {
                  if (prefs.paperTexture !== id) void setPrefs({ paperTexture: id });
                }}
                aria-checked={active}
                aria-pressed={active}
                aria-label={`Paper texture: ${TEXTURE_LABELS[id]}`}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: active ? "hsl(var(--ink))" : "hsl(var(--cream))",
                  color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                  border: "1px solid hsl(var(--ink))", borderRadius: 999,
                  cursor: disabled ? "not-allowed" : "pointer",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                {TEXTURE_LABELS[id]}
              </button>
            );
          })}
        </div>
        <Hint>
          {prefs.visionMode === "high_contrast"
            ? "Not available in High Contrast mode."
            : TEXTURE_HINTS[prefs.paperTexture]}
        </Hint>
      </div>

      {/* Font size — accessibility */}
      <div role="radiogroup" aria-labelledby="theme-studio-fontsize-label">
        <SubLabel id="theme-studio-fontsize-label">Text size</SubLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {FONT_SCALES.map((id, i) => {
            const active = prefs.fontScale === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                onClick={() => {
                  if (prefs.fontScale !== id) void setPrefs({ fontScale: id });
                }}
                aria-checked={active}
                aria-label={`Text size: ${FONT_SCALE_LABELS[id]}`}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: active ? "hsl(var(--ink))" : "hsl(var(--cream))",
                  color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                  border: "1px solid hsl(var(--ink))", borderRadius: 999,
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: `${10 * FONT_SCALE_VALUES[id]}px`,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                {FONT_SCALE_LABELS[id]}
              </button>
            );
          })}
        </div>
        <Hint>
          Scales every text element across Fig.
          Color contrast is verified at every size — see the Accessibility report.
        </Hint>
      </div>

      {/* Vision Mode + Color Vision preview (replaces old CVD preview) */}
      <VisionAccessibilitySections />

      {/* Reading style — plain-language toggle */}
      <ReadingStyleSection />

      {/* Reset */}
      <button
        type="button"
        onClick={() => resetPrefs()}
        style={{
          alignSelf: "flex-start",
          padding: "8px 14px",
          background: "transparent",
          color: "hsl(var(--ink-2))",
          border: "1px dashed hsl(var(--ink) / 0.5)",
          cursor: "pointer",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        Reset customizations
      </button>
    </div>
  );
}

// ─── Local subcomponents ────────────────────────────────────────────────────

function SubLabel({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <div
      id={id}
      className="caps"
      style={{
        color: "hsl(var(--ink-2))",
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
      }}
    >
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 6,
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 11,
        color: "hsl(var(--ink-2))",
        lineHeight: 1.35,
      }}
    >
      {children}
    </div>
  );
}
